import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';

import { FinchLogoFlat } from '@/components/base/logos/FinchLogoFlat';
import { NavBadge } from '@/components/navigation/NavBadge';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { TypeaheadSearch } from '@/components/search/TypeaheadSearch';
import { ProfileSettings } from '@/components/sidebar/ProfileSettings';
import {
  Navbar,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
} from '@/components/ui/catalyst/navbar';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useHeaderVisibility } from '@/contexts/HeaderVisibilityContext';
import { isCrashingOutEnabledForUser } from '@/lib/featureFlags';
import { cn } from '@/lib/utils';
import {
  NavBadgeCounts,
  useGetNavBadgeCountsQuery,
} from '@/services/api/navBadgeService';
import { selectUser } from '@/state/selectors/root-selectors';
// Define navigation items for mapping
type NavItem = {
  label: string;
  path: string;
  badgeKey?: keyof NavBadgeCounts;
  badgeTooltip?: string;
};

const allNavItems: NavItem[] = [
  { label: 'Intakes', path: '/intakes' },
  { label: 'Leads', path: '/leads' },
  {
    label: 'Matters',
    path: '/matters',
    badgeKey: 'initial_review_matters_count',
    badgeTooltip: 'Matters in initial review for firms you manage',
  },
  {
    label: 'Tasks',
    path: '/tasks',
    badgeKey: 'high_priority_tasks_count',
    badgeTooltip: 'Immediate and urgent tasks assigned to you',
  },
  { label: 'Firms', path: '/firms' },
  { label: 'Quant Mode', path: '/quant-mode' },
];

export const Header = () => {
  const location = useLocation();
  const { isHeaderVisible } = useHeaderVisibility();
  const user = useSelector(selectUser);
  const { data: badgeCounts } = useGetNavBadgeCountsQuery(undefined, {
    // Refetch when user returns to tab (more efficient than polling)
    refetchOnFocus: true,
  });

  const navItems = useMemo(() => {
    const isCrashoutUser = isCrashingOutEnabledForUser(user?.email);
    return allNavItems.filter((item) => {
      if (item.path === '/quant-mode' && !isCrashoutUser) return false;
      return true;
    });
  }, [user?.email]);

  return (
    <div
      className={cn(
        'flex-shrink-0 bg-header transition-all duration-300 ease-out',
        isHeaderVisible ? 'h-14 overflow-visible' : 'h-0 overflow-hidden',
      )}
    >
      <Navbar className="h-full w-full px-6">
        <NavbarSection>
          <FinchLogoFlat />
        </NavbarSection>

        <NavbarSection className="gap-4">
          {navItems.map((item) => {
            // Check if user is already on this path (ignore query params)
            const isCurrentPath = location.pathname === item.path;

            return (
              <NavLink
                className="dark"
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                onClick={(e) => {
                  // Prevent navigation if already on this path to preserve URL state (filters, etc.)
                  if (isCurrentPath) {
                    e.preventDefault();
                  }
                }}
              >
                {({ isActive }) => (
                  <div className="relative">
                    <NavbarItem current={isActive}>{item.label}</NavbarItem>
                    {item.badgeKey &&
                      badgeCounts &&
                      badgeCounts[item.badgeKey] > 0 && (
                        <NavBadge
                          count={badgeCounts[item.badgeKey]}
                          tooltip={item.badgeTooltip}
                        />
                      )}
                  </div>
                )}
              </NavLink>
            );
          })}
        </NavbarSection>

        <NavbarSpacer />

        <NavbarSection className="gap-4">
          <div className="w-80">
            <TypeaheadSearch />
          </div>
          <ThemeToggle className="text-white hover:bg-white/10 rounded-full" />
          <NotificationBell />
          <ProfileSettings />
        </NavbarSection>
      </Navbar>
    </div>
  );
};
